import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { formatPrice } from './utils/priceFormatter';
import './BookingConfirmation.css';

function BookingConfirmation() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get booking data from navigation state
  const stateData = location.state || {};
  
  // Check if this is a specialist booking
  const isSpecialist = stateData.isSpecialist || false;
  
  // Map the data with fallbacks for different property names
  const bookingData = {
    serviceName: isSpecialist 
      ? (stateData.specialist_name || 'Specialist')
      : (stateData.service_name || stateData.serviceName || 'Service'),
    serviceType: isSpecialist
      ? (stateData.specialist_title || 'Consultation')
      : (stateData.service_type || stateData.serviceType || 'Session'),
    date: stateData.date || stateData.booking_date || 'Not specified',
    time: stateData.time || stateData.booking_time || 'Not specified',
    duration: isSpecialist ? 'Consultation' : (stateData.service_duration || stateData.duration || 'Not specified'),
    price: stateData.service_price || stateData.price || 'Not specified',
    customerName: stateData.customer_name || stateData.customerName || '',
    customerEmail: stateData.customer_email || stateData.customerEmail || '',
    customerPhone: stateData.customer_phone || stateData.customerPhone || '',
    userNotes: stateData.user_notes || stateData.userNotes || '',
    bookingId: stateData.bookingId || stateData.id || '',
    paymentId: stateData.paymentId || '',
    paymentStatus: stateData.paymentStatus || stateData.payment_status || 'pending'
  };

  const isPaymentCompleted = bookingData.paymentStatus === 'completed';

  return (
    <div className="booking-confirmation-page">
      {/* Confirmation Header */}
      <section className="confirmation-header">
        <div className="container">
          <div className="header-content">
            <div className="success-icon-wrapper">
              <div className="success-glow"></div>
              <svg className="success-icon" width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z" fill="currentColor"/>
              </svg>
            </div>
            <h1 className="confirmation-title">
              {isPaymentCompleted ? 'Booking Confirmed!' : 'Booking Request Received'}
            </h1>
            <p className="confirmation-subtitle">
              {isPaymentCompleted 
                ? 'Thank you for choosing ManaWave. Your payment has been processed and your booking is confirmed!'
                : 'Thank you for choosing ManaWave. We\'ve received your booking request and will process it shortly.'}
            </p>
          </div>
        </div>
        <div className="wave-divider-confirmation"></div>
      </section>

      {/* Booking Summary */}
      <section className="booking-summary-section">
        <div className="container">
          <div className="summary-card">
            <h2 className="summary-card-title">Booking Summary</h2>
            <div className="summary-details">
              <div className="summary-row">
                <span className="summary-label">{isSpecialist ? 'Specialist' : 'Service'}</span>
                <span className="summary-value">{bookingData.serviceName}</span>
              </div>
              {!isSpecialist && (
                <div className="summary-row">
                  <span className="summary-label">Session Type</span>
                  <span className="summary-value">{bookingData.serviceType}</span>
                </div>
              )}
              {isSpecialist && bookingData.serviceType && (
                <div className="summary-row">
                  <span className="summary-label">Title</span>
                  <span className="summary-value">{bookingData.serviceType}</span>
                </div>
              )}
              <div className="summary-row">
                <span className="summary-label">Date</span>
                <span className="summary-value">{bookingData.date}</span>
              </div>
              <div className="summary-row">
                <span className="summary-label">Time</span>
                <span className="summary-value">{bookingData.time}</span>
              </div>
              <div className="summary-row">
                <span className="summary-label">Duration</span>
                <span className="summary-value">{bookingData.duration}</span>
              </div>
              <div className="summary-row summary-row-total">
                <span className="summary-label">Price</span>
                <span className="summary-value-total">{formatPrice(bookingData.price)}</span>
              </div>
              {isPaymentCompleted && bookingData.paymentId && (
                <div className="summary-row">
                  <span className="summary-label">Payment Status</span>
                  <span className="summary-value" style={{ color: '#10b981', fontWeight: '600' }}>
                    ✓ Paid
                  </span>
                </div>
              )}
            </div>
            
            {/* Customer Information */}
            {(bookingData.customerName || bookingData.customerEmail || bookingData.customerPhone) && (
              <div className="customer-details-section">
                <h3 className="customer-details-title">Your Contact Information</h3>
                <div className="summary-details">
                  {bookingData.customerName && (
                    <div className="summary-row">
                      <span className="summary-label">Name</span>
                      <span className="summary-value">{bookingData.customerName}</span>
                    </div>
                  )}
                  {bookingData.customerEmail && (
                    <div className="summary-row">
                      <span className="summary-label">Email</span>
                      <span className="summary-value">{bookingData.customerEmail}</span>
                    </div>
                  )}
                  {bookingData.customerPhone && (
                    <div className="summary-row">
                      <span className="summary-label">Phone</span>
                      <span className="summary-value">{bookingData.customerPhone}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* User Notes */}
            {bookingData.userNotes && (
              <div className="notes-section">
                <h3 className="notes-title">Your Notes</h3>
                <p className="notes-text">{bookingData.userNotes}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Status Indicator */}
      <section className="status-section">
        <div className="container">
          <div className="status-card">
            <div className="status-indicator">
              <div className="status-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z" fill="currentColor"/>
                </svg>
              </div>
              <div className="status-content">
                <h3 className="status-label">
                  {isPaymentCompleted ? 'Confirmed' : 'Pending Confirmation'}
                </h3>
                <p className="status-description">
                  {isPaymentCompleted
                    ? 'Your booking has been confirmed! You will receive session details via email shortly.'
                    : 'Our team will review your booking request and confirm availability within 24 hours.'}
                </p>
              </div>
            </div>
            <div className="progress-steps">
              <div className="step step-completed">
                <div className="step-circle">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z" fill="currentColor"/>
                  </svg>
                </div>
                <span className="step-label">Request Submitted</span>
              </div>
              <div className="step-connector"></div>
              <div className="step step-active">
                <div className="step-circle"></div>
                <span className="step-label">Admin Review</span>
              </div>
              <div className="step-connector"></div>
              <div className="step step-pending">
                <div className="step-circle"></div>
                <span className="step-label">Confirmation</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Next Steps */}
      <section className="next-steps-section">
        <div className="container">
          <div className="next-steps-content">
            <h2 className="section-title">What Happens Next</h2>
            <div className="steps-list">
              <div className="step-item">
                <div className="step-item-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 4H4C2.9 4 2.01 4.9 2.01 6L2 18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 8L12 13L4 8V6L12 11L20 6V8Z" fill="currentColor"/>
                  </svg>
                </div>
                <div className="step-item-content">
                  <h4 className="step-item-title">Confirmation Email</h4>
                  <p className="step-item-text">
                    You'll receive an email notification once your booking is confirmed by our team.
                  </p>
                </div>
              </div>
              {!isPaymentCompleted && (
              <div className="step-item">
                <div className="step-item-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 17C11.45 17 11 16.55 11 16C11 15.45 11.45 15 12 15C12.55 15 13 15.45 13 16C13 16.55 12.55 17 12 17ZM13 13H11V7H13V13Z" fill="currentColor"/>
                  </svg>
                </div>
                <div className="step-item-content">
                  <h4 className="step-item-title">Payment Link</h4>
                  <p className="step-item-text">
                    After confirmation, you'll receive a secure payment link to complete your booking.
                  </p>
                </div>
              </div>
              )}
              <div className="step-item">
                <div className="step-item-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z" fill="currentColor"/>
                  </svg>
                </div>
                <div className="step-item-content">
                  <h4 className="step-item-title">Session Preparation</h4>
                  <p className="step-item-text">
                    We'll send you preparation instructions and session details before your scheduled time.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Action Options */}
      <section className="action-section">
        <div className="container">
          <div className="action-buttons">
            <button 
              className="btn-primary-action"
              onClick={() => navigate('/my-bookings')}
            >
              View My Bookings
            </button>
            <a 
              href="tel:+447356083797"
              className="btn-secondary-action"
              style={{ textDecoration: 'none', display: 'inline-block' }}
            >
              Contact Support: +447356083797
            </a>
          </div>
        </div>
      </section>

      {/* Reassurance Section */}
      <section className="reassurance-section">
        <div className="container">
          <div className="reassurance-content">
            <div className="reassurance-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 1L3 5V11C3 16.55 6.16 21.74 12 23C17.84 21.74 21 16.55 21 11V5L12 1ZM12 7C13.4 7 14.8 7.6 15.7 8.3L14.2 9.8C13.6 9.3 12.8 9 12 9C10.3 9 9 10.3 9 12C9 13.7 10.3 15 12 15C13.7 15 15 13.7 15 12H17C17 15.3 14.3 18 12 18C9.7 18 7 15.3 7 12C7 8.7 9.7 6 12 6Z" fill="currentColor"/>
              </svg>
            </div>
            <p className="reassurance-text">
              Your information is handled with the utmost care and privacy. All sessions are conducted 
              with respect, intentional energy work, and professional practices. We're here to support 
              your wellness journey.
            </p>
          </div>
        </div>
      </section>

      {/* Gentle Closure */}
      <footer className="confirmation-footer">
        <div className="wave-footer-closure"></div>
      </footer>
    </div>
  );
}

export default BookingConfirmation;

